import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ScrollText, BookOpen, ArrowRight } from 'lucide-react';
import { hadithApi } from '../services/api';
import { SectionHeader, PageLoading, GlassCard } from '../components/shared';

const HadithCollections = () => {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hadithApi.getCollections()
      .then(res => setCollections(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoading />;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="hadith-collections-page">
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          overline={t('hadith.library_overline')}
          title={t('hadith.library_title')}
          subtitle={t('hadith.library_subtitle')}
        />

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {collections.map((c, idx) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
            >
              <Link
                to={`/hadith/${c.slug}`}
                data-testid={`hadith-collection-card-${c.slug}`}
                className="block"
              >
                <GlassCard className="hover:border-primary/40 transition-all duration-300 group cursor-pointer h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                      <ScrollText className="w-7 h-7 text-primary" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-text-muted group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </div>

                  <p className="font-arabic text-3xl text-text-primary text-right mb-1" dir="rtl">
                    {c.name_ar}
                  </p>
                  <h3 className="text-xl font-serif text-text-primary mt-3">
                    {c.name_fr || c.name_en}
                  </h3>
                  <p className="text-sm text-text-muted mt-1">
                    {t('hadith.compiled_by', { name: c.compiler_en })}
                  </p>

                  {c.description_fr && (
                    <p className="text-sm text-text-secondary mt-4 line-clamp-3">
                      {c.description_fr}
                    </p>
                  )}

                  <div className="flex items-center gap-4 mt-5 pt-4 border-t border-border text-xs text-text-muted">
                    <span className="flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5" />
                      {t('hadith.hadiths_count', { count: c.total_hadiths.toLocaleString() })}
                    </span>
                  </div>
                </GlassCard>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HadithCollections;
